/* Sportedix — collection/search filtering, sorting and "load more" via the Section Rendering API */
(() => {
  if (customElements.get('facets-root')) return;
  const SX = window.Sportedix || {};

  class FacetsRoot extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-facets-form]');
      this.panel = this.querySelector('[data-facets-panel]');
      this.cache = new Map();
      this.debounced = this.debounce(() => this.submit(), 450);

      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-facets-form]') || e.target.matches('[data-facets-sort]')) {
          if (e.target.type === 'number') this.debounced(); else this.submit();
        }
      });
      this.addEventListener('input', (e) => { if (e.target.type === 'number' && e.target.closest('[data-facets-form]')) this.debounced(); });
      if (this.form) this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); this.load(new URL(link.href, window.location.href).search.slice(1)); return; }
        const more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); this.loadMore(more); return; }
        if (e.target.closest('[data-facets-open]')) { this.openPanel(e.target.closest('[data-facets-open]')); return; }
        if (e.target.closest('[data-facets-close]')) this.closePanel();
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.panel && this.panel.classList.contains('is-open')) this.closePanel(); });
      this._pop = () => this.load(window.location.search.slice(1), false);
      window.addEventListener('popstate', this._pop);
    }
    disconnectedCallback() { window.removeEventListener('popstate', this._pop); document.body.classList.remove('sx-lock'); }
    debounce(fn, wait) { let t; return () => { clearTimeout(t); t = setTimeout(fn, wait); }; }
    isDrawer() { return !this.classList.contains('sx-facets--sidebar') || !window.matchMedia('(min-width: 990px)').matches; }
    openPanel(trigger) {
      if (!this.panel) return;
      this.trigger = trigger;
      this.panel.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('sx-lock');
      if (SX.trapFocus) SX.trapFocus(this.panel.querySelector('.sx-facets__sheet'));
    }
    closePanel() {
      if (!this.panel || !this.panel.classList.contains('is-open')) return;
      this.panel.classList.remove('is-open');
      document.body.classList.remove('sx-lock');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      if (SX.removeTrapFocus) SX.removeTrapFocus(this.trigger);
    }
    queryFromForm() {
      const fd = this.form ? new FormData(this.form) : new FormData();
      const sort = this.querySelector('[data-facets-sort]');
      if (sort && !this.form) fd.set('sort_by', sort.value);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v !== '') params.append(k, v);
      return params.toString();
    }
    submit() { this.load(this.queryFromForm()); }
    async load(query, push = true) {
      const results = this.querySelector('[data-facets-results]');
      if (results) results.classList.add('is-loading');
      const url = `${window.location.pathname}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;
      try {
        let html = this.cache.get(url);
        if (!html) { html = await (await fetch(url)).text(); this.cache.set(url, html); }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.swap(doc);
        if (push) window.history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
      } catch (e) {
        window.location.href = `${window.location.pathname}?${query}`;
      } finally {
        const fresh = this.querySelector('[data-facets-results]');
        if (fresh) fresh.classList.remove('is-loading');
      }
    }
    swap(doc) {
      const types = this.querySelector('[data-facets-types]');
      const nextTypes = doc.querySelector('[data-facets-types]');
      if (types && nextTypes) types.replaceWith(nextTypes);
      ['[data-facets-results]', '[data-facets-chips]', '[data-facets-count]', '[data-facets-apply]', '[data-facets-open]'].forEach((sel) => {
        const cur = this.querySelector(sel);
        const next = doc.querySelector(sel);
        if (cur && next) cur.innerHTML = next.innerHTML;
      });
      this.querySelectorAll('[data-facet-body]').forEach((body) => {
        const next = doc.querySelector(`[data-facet-body="${body.dataset.facetBody}"]`);
        if (next) body.innerHTML = next.innerHTML;
      });
      this.querySelectorAll('details.sx-facet').forEach((d) => {
        const next = doc.getElementById(d.id);
        const summary = next && next.querySelector('summary');
        if (summary) d.querySelector('summary').innerHTML = summary.innerHTML;
      });
      const sort = this.querySelector('[data-facets-sort]');
      const nextSort = doc.querySelector('[data-facets-sort]');
      if (sort && nextSort) sort.value = nextSort.value;
      if (SX.initReveal) SX.initReveal(this);
    }
    async loadMore(btn) {
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(btn.href, window.location.href);
        url.searchParams.set('section_id', this.sectionId);
        const doc = new DOMParser().parseFromString(await (await fetch(url)).text(), 'text/html');
        const grid = this.querySelector('[data-facets-grid]');
        const items = doc.querySelectorAll('[data-facets-grid] > li');
        const firstNew = items[0];
        items.forEach((li) => grid.appendChild(li));
        const wrap = btn.closest('.sx-load-more');
        const nextWrap = doc.querySelector('.sx-load-more');
        if (wrap && nextWrap) wrap.innerHTML = nextWrap.innerHTML; else if (wrap) wrap.remove();
        const focusTarget = firstNew && grid.querySelector(`li:nth-child(${grid.children.length - items.length + 1}) a[href]:not([tabindex="-1"])`);
        if (focusTarget) focusTarget.focus({ preventScroll: true });
        if (SX.initReveal) SX.initReveal(this);
      } catch (e) {
        window.location.href = btn.href;
      }
    }
  }
  customElements.define('facets-root', FacetsRoot);
})();
